<template>
  <div class="page">
    <div class="banner">
      <div class="container d-flex justify-content-center"></div>
      <div class="container d-flex justify-content-center">
        <img class="banner-logo" :src="logo" alt="Company Logo" @error="onImageError">
      </div>
    </div>

    <div class="container main-content">
      <div id="swal-container"></div>

      <div class="form-container">
        <h3 class="form-title">Fill in the Details</h3>

        <div v-if="loading" class="text-center my-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading form...</span>
          </div>
          <p>Loading form...</p>
        </div>

        <div v-if="isLoadingPrice" class="text-center my-4">
          <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">Looking up price...</span>
          </div>
          <p>Looking up price for your location...</p>
        </div>

        <div v-if="priceLookupError" class="alert alert-danger text-center my-4" role="alert">
          <strong>Price Not Available:</strong> {{ priceLookupError }}
        </div>

        <!-- R7: features loader. Shows while we confirm whether the customer is on
             the Farm Depot price-matrix plan. Without this, the legacy form could
             flash briefly on screen before being replaced by multi-brand UI. -->
        <div v-if="!featuresLoaded" class="text-center my-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Verifying payment options...</span>
          </div>
          <p>Verifying payment options...</p>
        </div>

        <!-- Multi-brand selection UI -->
        <div v-if="multiBrandMode && usesPriceMatrix && !loading" class="multi-brand-section mb-4">
          <h4 class="form-title mb-3">Select Products</h4>

          <!-- Location Dropdown -->
          <div v-if="!selectedLocationId" class="form-field mb-4">
            <label for="location-select">Store Location <span class="required">*</span></label>
            <select
                id="location-select"
                class="form-input"
                @change="onLocationChange"
                :disabled="isSubmitting">
              <option value="" disabled selected>-- Select a Location --</option>
              <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
            </select>
          </div>

          <!-- Brand Selection Grid (shown once location is selected) -->
          <div v-if="selectedLocationId">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h5 class="mb-0">Products at {{ locations.find(l => l.id === selectedLocationId)?.name }}</h5>
              <button type="button" class="btn btn-link p-0" @click="selectedLocationId = null; availableBrands = []; selectedBrands = {};">Change Store</button>
            </div>

            <div v-if="isLoadingPrice" class="text-center my-3">
              <div class="spinner-border text-success" role="status">
                <span class="visually-hidden">Loading brands...</span>
              </div>
              <p>Loading brands...</p>
            </div>

            <div v-else-if="availableBrands.length === 0" class="alert alert-warning">
              No brands available for this location.
            </div>

            <!-- Brand List (shown once location is selected) -->
            <div v-else class="brand-list">
              <div
                  v-for="brand in availableBrands"
                  :key="brand.id"
                  class="brand-list-row"
                  :class="{ 'selected': selectedBrands[brand.id] }">
                <img v-if="brand.logoUrl" :src="brand.logoUrl" class="brand-logo-img" :alt="brand.brandName" @error="brand.logoUrl = null" />
                <div class="brand-list-info">
                  <label :for="'qty-' + brand.id" class="brand-name">{{ brand.brandName }}</label>
                  <div class="brand-price">{{ formatCurrency(brand.price || 0) }} per unit</div>
                </div>
                <div class="brand-list-actions">
                  <div class="brand-quantity">
                    <label :for="'qty-' + brand.id" class="qty-label">Qty:</label>
                    <input
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        :id="'qty-' + brand.id"
                        class="form-input quantity-input"
                        v-model="selectedBrands[brand.id].quantity"
                        @input="checkStockWarnings"
                        :disabled="isSubmitting || brand.stockCount === 0" />
                  </div>
                  <span v-if="selectedBrands[brand.id]?.stockWarning" class="stock-exceeded-warning">
                    {{ selectedBrands[brand.id].stockWarning }}
                  </span>
                  <span class="line-total">{{ formatCurrency((brand.price || 0) * (selectedBrands[brand.id]?.quantity || 0)) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!loading">
          <div v-if="paymentAmountDetails" class="form-field payment-field mb-4 pb-4 border-bottom">
            <label :for="paymentAmountDetails.id || 'default-amount'">
              {{ paymentAmountDetails.label }}
              <span v-if="paymentAmountDetails.required" class="required">*</span>
            </label>
            <input
                :id="paymentAmountDetails.id || 'default-amount'"
                type="number"
                v-model.number="paymentAmountDetails.value"
                :required="paymentAmountDetails.required"
                class="form-input"
                :placeholder="paymentAmountDetails.placeholder || 'Enter amount'"
                @input="updateSummary"
                step="0.01"
                min="0"
                :disabled="isSubmitting || multiBrandMode" />
            <p v-if="paymentAmountDetails.helpText" class="help-text">{{ paymentAmountDetails.helpText }}</p>
          </div>
          <div v-else-if="!loading && formFields.length === 0 && !paymentAmountDetails" class="text-center text-danger my-3">
            Error: No payment fields could be loaded for this form. Payment cannot proceed.
          </div>

          <div v-if="showOptionalUnitPrice && unitPriceDetails" class="form-field unit-price-field mb-4 pb-4 border-bottom">
            <label :for="unitPriceDetails.id || 'optional-unit-price'">
              {{ unitPriceDetails.label }}
              <span v-if="unitPriceDetails.required" class="required">*</span>
            </label>
            <input
                :id="unitPriceDetails.id || 'optional-unit-price'"
                type="number"
                v-model.number="unitPriceDetails.value"
                :required="unitPriceDetails.required"
                class="form-input"
                :placeholder="unitPriceDetails.placeholder || 'Enter unit price'"
                @input="updateSummary"
                min="0"
                step="0.01"
                :disabled="isSubmitting" />
            <p v-if="unitPriceDetails.helpText" class="help-text">{{ unitPriceDetails.helpText }}</p>
          </div>

          <div v-if="formFields.length > 0" class="custom-fields-section border-t pt-4 mt-4">
            <h4 class="text-md font-semibold mb-3">Additional Information</h4>
            <div v-for="(field, index) in formFields" :key="field.id || 'field-' + index" class="form-field mb-4 pb-4 border-bottom" v-show="shouldShowField(field)">

              <div v-if="field.SetUnit">
                <label :for="'quantity-' + index">
                  {{ field.label }}
                  <span class="unit-details">({{ formatCurrency(field.unitPrice) }} per {{ field.unitLabel || 'unit' }})</span>
                  <span v-if="field.required" class="required">*</span>
                  <span v-if="field.isInstallmentPayment" class="installment-badge">(Installments Enabled)</span>
                </label>
                <div class="unit-input-group mb-2">
                  <input
                      :id="'quantity-' + index"
                      type="number"
                      v-model.number="field.quantity"
                      :required="field.required"
                      class="form-input quantity-input"
                      placeholder="Quantity (e.g., 1)"
                      min="0"
                      step="1"
                      @input="handleInstallmentTotalChange(field)"
                      :disabled="isSubmitting"/>
                  <span class="unit-total">Item Total: {{ formatCurrency(field.unitPrice * (field.quantity || 0)) }}</span>
                </div>
                <div v-if="field.isInstallmentPayment" class="mt-2">
                  <label :for="'installment-paid-' + index">
                    Amount to Pay Now for this Item
                    <span v-if="field.required" class="required">*</span>
                  </label>
                  <input
                      :id="'installment-paid-' + index"
                      type="number"
                      v-model.number="field.installmentDetails.amountPaidNow"
                      class="form-input"
                      placeholder="Leave blank or 0 to pay full item total now"
                      step="0.01"
                      min="0"
                      :max="(field.unitPrice || 0) * (field.quantity || 0)"
                      @input="handleAmountPaidNowChange(field)"
                      :disabled="isSubmitting"
                  />
                  <p class="remaining-due mt-1">Remaining Due for this item: {{ formatCurrency(calculateItemRemainingDue(field)) }}</p>
                </div>
                <p v-if="field.helpText" class="help-text">{{ field.helpText }}</p>
              </div>

              <div v-else-if="field.isInstallmentPayment">
                <label :for="'installment-total-' + index">
                  {{ field.label }} (Total Amount)
                  <span v-if="field.required" class="required">*</span>
                </label>
                <input
                    :id="'installment-total-' + index"
                    type="number"
                    v-model.number="field.value"
                    :required="field.required"
                    class="form-input mb-2"
                    placeholder="Total Amount for this item"
                    step="0.01"
                    min="0"
                    @input="handleInstallmentTotalChange(field)"
                    :disabled="isSubmitting"
                />
                <label :for="'installment-paid-' + index">
                  Amount to Pay Now
                  <span v-if="field.required" class="required">*</span>
                </label>
                <input
                    :id="'installment-paid-' + index"
                    type="number"
                    v-model.number="field.installmentDetails.amountPaidNow"
                    :required="field.required"
                    class="form-input"
                    placeholder="Amount paid now"
                    step="0.01"
                    min="0"
                    :max="field.value || 0"
                    @input="handleAmountPaidNowChange(field)"
                    :disabled="isSubmitting"
                />
                <p class="remaining-due mt-1">Remaining Due for this item: {{ formatCurrency(calculateItemRemainingDue(field)) }}</p>
                <p v-if="field.helpText" class="help-text">{{ field.helpText }}</p>
              </div>

              <div v-else>
                <label :for="'field-input-' + index">
                  {{ field.label }}<span v-if="field.required" class="required">*</span>
                </label>
                <input v-if="field.type === 'text'" type="text" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || ''" :disabled="isSubmitting"/>
                <input v-else-if="field.type === 'payment'" type="number" v-model.number="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || 'Enter amount'" step="0.01" min="0" @input="updateSummary" :disabled="isSubmitting"/>
                <input v-else-if="field.type === 'email'"
                       type="email"
                       v-model="field.value"
                       :required="field.required"
                       class="form-input"
                       :id="'field-input-' + index"
                       :placeholder="field.placeholder || 'Enter email address'"
                       :disabled="isSubmitting"/>
                <input v-else-if="field.type === 'tel'"
                       type="tel"
                       v-model="field.value"
                       :required="field.required"
                       class="form-input"
                       :id="'field-input-' + index"
                       :placeholder="field.placeholder || 'Enter phone number'"
                       :disabled="isSubmitting"/>
                <textarea v-else-if="field.type === 'textarea'" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :placeholder="field.placeholder || ''" :disabled="isSubmitting"></textarea>
                <select v-else-if="field.type === 'select'" v-model="field.value" :required="field.required" class="form-input" :id="'field-input-' + index" :disabled="isSubmitting">
                  <option value="" disabled>-- {{ field.placeholder || 'Select an option' }} --</option>
                  <option v-for="(option,oIdx) in field.options" :key="oIdx" :value="option">{{ option }}</option>
                </select>
                <input v-else-if="field.type === 'date'" type="date" v-model="field.value" :required="field.required" class="form-input date-input" :id="'field-input-' + index" :disabled="isSubmitting"/>
                <div v-else-if="field.type === 'checkbox'" class="checkbox-field">
                  <input type="checkbox" :id="'field-input-'+index" v-model="field.value" :required="field.required" :disabled="isSubmitting"/>
                  <label :for="'field-input-'+index" class="ml-2">{{ field.options?.[0] || 'Confirm' }}</label>
                </div>
                <div v-else-if="field.type === 'radio'" class="radio-group">
                  <div v-for="(option, oIdx) in field.options" :key="oIdx" class="radio-option">
                    <input type="radio" :id="'radio-' + index + '-' + oIdx" :value="option" v-model="field.value" :name="'radio-group-' + index" :required="field.required" :disabled="isSubmitting"/>
                    <label :for="'radio-' + index + '-' + oIdx">{{ option }}</label>
                  </div>
                </div>
                <div v-else-if="field.type === 'checkbox-group'" class="checkbox-group">
                  <div v-for="(option, oIdx) in field.options" :key="oIdx" class="checkbox-option">
                    <input type="checkbox" :id="'checkbox-' + index + '-' + oIdx" v-model="field.value[option]" :disabled="isSubmitting"/>
                    <label :for="'checkbox-' + index + '-' + oIdx">{{ option }}</label>
                  </div>
                </div>
                <div v-else-if="field.type === 'file'" class="file-field">
                  <input type="file" :id="'field-input-' + index" @change="e => handleFileUpload(e, field)" :required="field.required" :disabled="isSubmitting"/>
                  <span v-if="field.displayValue" class="file-name-display">{{ field.displayValue }}</span>
                  <p class="help-text" style="color: #dc3545; font-weight: bold;">Note: File content will not be uploaded with this form.</p>
                </div>
                <p v-if="field.helpText" class="help-text">{{ field.helpText }}</p>
              </div>
            </div>
          </div>
          <div v-else-if="paymentAmountDetails && formFields.length === 0" class="text-center text-muted my-3 italic">
            (No additional fields configured)
          </div>

          <div class="navigation-buttons mt-5">
            <button type="submit" @click="submitForm" class="submit-btn" :disabled="isSubmitting || loading || (!paymentAmountDetails && formFields.length === 0 && !hasPaymentDefiningCustomField) || (multiBrandMode && !hasMultiBrandSelections())">
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
              {{ isSubmitting ? 'Processing...' : 'Make Payment' }}
            </button>
          </div>

        </div>
      </div>

      <div class="summary-container">
        <div class="card sticky-top">
          <div class="card-body">
            <h3 class="card-title text-center mb-3">Summary</h3>

            <!-- Multi-brand cart items -->
            <div v-if="multiBrandMode && hasMultiBrandSelections()" class="summary-section mb-3">
              <h6 class="fw-bold mb-2">Cart Items</h6>
              <div v-for="(item, brandId) in selectedBrands" :key="brandId" class="summary-item" v-show="item.quantity > 0">
                <span>{{ item.name }}:</span>
                <span class="fw-bold">{{ item.quantity }} x {{ formatCurrency(item.price) }} = {{ formatCurrency(item.price * item.quantity) }}</span>
              </div>
            </div>

            <div v-if="paymentAmountDetails && !multiBrandMode" class="summary-item">
              <span>{{ paymentAmountDetails.label }}:</span>
              <span class="fw-bold">{{ formatCurrency(paymentAmountDetails.value) }}</span>
            </div>
            <div v-else-if="multiBrandMode && hasMultiBrandSelections()" class="summary-item">
              <span>Total:</span>
              <span class="fw-bold">{{ formatCurrency(multiBrandCartTotal()) }}</span>
            </div>
            <div v-if="showOptionalUnitPrice && unitPriceDetails && unitPriceDetails.value != null" class="summary-item">
              <span>{{ unitPriceDetails.label }}:</span>
              <span class="fw-bold">{{ formatCurrency(unitPriceDetails.value) }}</span>
            </div>
            <div v-for="field in summaryCustomFields" :key="'summary-' + field.id" class="summary-item">
              <span>{{ field.label }}:</span>
              <span class="fw-bold">
                <span v-if="field.SetUnit && field.isInstallmentPayment">
                  {{ getUnitInstallmentSummaryText(field) }}
                </span>
                <span v-else-if="field.SetUnit">
                  {{ field.quantity || 0 }} x {{ formatCurrency(field.unitPrice) }} = {{ formatCurrency((field.unitPrice || 0) * (field.quantity || 0)) }}
                </span>
                <span v-else-if="field.isInstallmentPayment">
                  {{ formatCurrency(field.installmentDetails?.amountPaidNow) }} (of {{ formatCurrency(field.value) }})
                </span>
                <span v-else-if="field.type === 'payment'">{{ formatCurrency(field.value) }}</span>
                <span v-else>{{ getSummaryDisplayValue(field) }}</span>
              </span>
            </div>

            <hr class="my-3">
            <div class="summary-item total-amount">
              <span class="fw-bold">Order Total:</span>
              <span class="fw-bold">{{ formattedTotalAmount }}</span>
            </div>
            <div class="summary-item total-amount">
              <span class="fw-bold">Amount Paying Now:</span>
              <span class="fw-bold">{{ formattedTotalAmountPaidNow }}</span>
            </div>
            <div v-if="totalRemainingDue > 0" class="summary-item total-amount text-warning">
              <span class="fw-bold">Remaining Due Later:</span>
              <span class="fw-bold">{{ formattedTotalRemainingDue }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from "sweetalert2";
import { mapState } from 'vuex'; // If using Vuex

// --- Assume generateUUID function exists globally or import it ---
// Example: function generateUUID() { /* implementation */ }

export default {
  name: "PaymentViewRefactoredSynced",
  data() {
    return {
      companyName: "",
      companyApiKey: null, // Stores the API key fetched from the backend
      paymentAmountDetails: null, // Details for the primary 'amount' field
      unitPriceDetails: null, // Details for the optional 'unit_price' field
      showOptionalUnitPrice: false, // Flag to show the unit price field
      formFields: [], // Array to hold all custom form fields
      loading: false, // Indicates if the form is initially loading
      isSubmitting: false, // Indicates if the form is currently being submitted
      logo: null, // URL for the company logo
      defaultImageUrl: 'https://placehold.co/300x100/40434A/FFF?text=Logo', // Placeholder logo
      // Farm Depot price lookup state
      locationId: null, // location_id from query params
      brandId: null, // brand_id from query params
      priceLookupError: null, // Error message if price not found
      isLoadingPrice: false, // Loading state for price lookup
      // Multi-brand selection state
      multiBrandMode: false, // true when no URL params AND usesPriceMatrix is true
      usesPriceMatrix: false, // true if this company has paid for Farm Depot
      featuresLoaded: false, // R7: set true after fetchCompanyFeatures() resolves; gates form render to prevent legacy-form flash when flag is on
      preselectedBrandId: null, // R7/T8: brandId from URL params, used to pre-highlight brand in grid (when flag is on)
      locations: [], // Array of {id, name} from GET /api/locations
      selectedLocationId: null, // Always start fresh on each visit so the location dropdown shows first; the "Change Store" button lets users switch later. Saved selections still get written to localStorage (see onLocationChange).
      availableBrands: [], // Prices filtered by selected location
      selectedBrands: {} // Map of brandId -> { quantity, price, name, id }
    };
  },
  computed: {
    // Checks if any custom field defines a payment amount (unit price, installment, or payment type)
    hasPaymentDefiningCustomField() {
      return this.formFields.some(f => f.SetUnit || f.isInstallmentPayment || f.type === 'payment');
    },
    // Calculates the total order value based on all relevant fields
    totalAmount() {
      let total = 0;

      // Add multi-brand cart items (only when NOT showing in paymentAmountDetails)
      if (this.multiBrandMode && !this.paymentAmountDetails) {
        total += this.multiBrandCartTotal();
      }

      // Add primary payment amount if present
      if (this.paymentAmountDetails) {
        total += parseFloat(this.paymentAmountDetails.value) || 0;
      }
      // Add amounts from custom fields
      this.formFields.forEach(field => {
        if (this.shouldShowField(field)) { // Only include visible fields
          if (field.SetUnit) { // Unit price * quantity
            total += (parseFloat(field.unitPrice) || 0) * Math.max(0, parseInt(field.quantity, 10) || 0);
          } else if (field.isInstallmentPayment) { // Total value for installment items
            total += parseFloat(field.value) || 0;
          } else if (field.type === 'payment') { // Simple payment fields
            total += parseFloat(field.value) || 0;
          }
        }
      });
      return total;
    },
    // Calculates the amount the user is actually paying in this transaction
    totalAmountPaidNow() {
      let totalPaid = 0;

      // Add multi-brand cart items (pay full for all, unless in paymentAmountDetails)
      if (this.multiBrandMode && !this.paymentAmountDetails) {
        totalPaid += this.multiBrandCartTotal();
      }

      // Add primary payment amount if present
      if (this.paymentAmountDetails) {
        totalPaid += parseFloat(this.paymentAmountDetails.value) || 0;
      }
      // Add amounts being paid now from custom fields
      this.formFields.forEach(field => {
        if (this.shouldShowField(field)) {
          if (field.SetUnit && field.isInstallmentPayment) {
            const itemTotal = (parseFloat(field.unitPrice) || 0) * Math.max(0, parseInt(field.quantity, 10) || 0);
            const amountEntered = field.installmentDetails?.amountPaidNow;
            // If no amount entered or 0, assume paying full item total (if > 0)
            if (amountEntered == null || isNaN(amountEntered) || amountEntered <= 0) { // Allow 0 to mean 'pay full'
              if (itemTotal > 0) totalPaid += itemTotal;
            } else {
              // Otherwise, pay the entered amount, capped by item total
              totalPaid += Math.max(0, Math.min(amountEntered, itemTotal));
            }
          } else if (field.SetUnit) {
            // For unit price items (no installments), pay full item total based on quantity
            totalPaid += (parseFloat(field.unitPrice) || 0) * Math.max(0, parseInt(field.quantity, 10) || 0);
          } else if (field.isInstallmentPayment) {
            // For non-SetUnit installments, user must enter amount > 0 to pay
            const itemTotal = parseFloat(field.value) || 0;
            const amountEntered = field.installmentDetails?.amountPaidNow;
            if (amountEntered != null && !isNaN(amountEntered) && amountEntered > 0) {
              totalPaid += Math.max(0, Math.min(amountEntered, itemTotal));
            }
          } else if (field.type === 'payment') {
            // For simple payment fields, pay the entered value
            totalPaid += parseFloat(field.value) || 0;
          }
        }
      });
      return Math.max(0, totalPaid); // Ensure total paid is not negative
    },
    // Calculates the remaining amount due after this payment
    totalRemainingDue() {
      return Math.max(0, this.totalAmount - this.totalAmountPaidNow);
    },
    // Filters form fields to show in the summary sidebar
    summaryCustomFields() {
      return this.formFields.filter(f =>
          this.shouldShowField(f) && // Only visible fields
          ( // Include if:
              f.type === 'payment' || // It's a payment field
              (f.SetUnit && (parseInt(f.quantity, 10) || 0) > 0) || // It uses unit pricing AND has quantity > 0
              f.isInstallmentPayment || // It's an installment payment
              // Or it has a non-empty value (considering different types)
              (this.getRawValue(f) != null && this.getRawValue(f) !== '') ||
              (f.type === 'checkbox' && f.value === true) ||
              (f.type === 'checkbox-group' && Object.values(f.value || {}).some(v => v === true)) ||
              (f.type === 'file' && f.displayValue)
          )
      );
    },
    // Formats the total amount as currency
    formattedTotalAmount() {
      return this.formatCurrency(this.totalAmount);
    },
    // Formats the amount paid now as currency
    formattedTotalAmountPaidNow() {
      return this.formatCurrency(this.totalAmountPaidNow);
    },
    // Formats the remaining due amount as currency
    formattedTotalRemainingDue() {
      return this.formatCurrency(this.totalRemainingDue);
    },
  },
  beforeMount() {
    // Get company alias from the route parameters
    this.companyName = this.$route.params.company;
    console.log(`[DEBUG] Company Alias from route: ${this.companyName}`);

    // Extract location_id and brand_id from query params for Farm Depot price lookup
    this.locationId = this.$route.query.location_id ? parseInt(this.$route.query.location_id, 10) : null;
    this.brandId = this.$route.query.brand_id ? parseInt(this.$route.query.brand_id, 10) : null;
    console.log(`[DEBUG] Price lookup params - location_id: ${this.locationId}, brand_id: ${this.brandId}`);

    // Determine mode: deep-link (has location_id AND brand_id) vs multi-brand selection (neither)
    // multiBrandMode is set to true only after usesPriceMatrix is confirmed true
    this.multiBrandMode = !this.locationId && !this.brandId;

    if (this.companyName) {
      this.getForm(); // Fetch form configuration and API key
    } else {
      console.error("[DEBUG] Company alias is missing from the URL.");
      Swal.fire({ icon: 'error', title: 'Error', text: 'Company identifier is missing.' });
    }
  },
  methods: {
    // Fetches company features to determine Farm Depot access
    async fetchCompanyFeatures() {
      // R7: wrap in try/finally so featuresLoaded=true is set in EVERY path
      // (success, !res.ok, catch, early return). This guarantees the form never
      // renders before we know whether the customer is allowed to see legacy UI.
      try {
        if (!this.companyApiKey || typeof ChickenLittle === 'undefined') {
          this.usesPriceMatrix = false;
          return;
        }
        const res = await fetch(`${ChickenLittle}api/company/features`, {
          headers: { 'Api-Key': this.companyApiKey }
        });
        if (res.ok) {
          const data = await res.json();
          this.usesPriceMatrix = data.usesPriceMatrix === true;
        } else {
          this.usesPriceMatrix = false;
        }
      } catch (e) {
        this.usesPriceMatrix = false;
      } finally {
        this.featuresLoaded = true;
      }
    },

    // Fetches the company logo using the API key
    async getLogo(){
      const apikey = this.companyApiKey;
      console.log("[DEBUG LOGO] Attempting to get logo using API Key:", apikey);
      // Check if API key and gateway URL are available
      // Assuming 'gateway' is a globally defined variable or available elsewhere
      if (apikey && typeof gateway !== 'undefined') {
        this.logo = gateway + `logo?apikey=${apikey}`; // Construct logo URL
        console.log("[DEBUG LOGO] Setting logo URL:", this.logo);
      } else {
        console.warn("[DEBUG LOGO] API Key unavailable or gateway variable missing when trying to fetch logo.");
        this.logo = this.defaultImageUrl; // Use default placeholder
      }
    },

    // Lookup price by location and brand for Farm Depot payment links
    async lookupPriceByLocationBrand() {
      if (!this.locationId || !this.brandId) {
        return;
      }

      this.isLoadingPrice = true;
      this.priceLookupError = null;
      console.log(`[DEBUG PRICE LOOKUP] Looking up price for locationId: ${this.locationId}, brandId: ${this.brandId}`);

      try {
        // Assuming 'ChickenLittle' is the base API URL for price lookup
        if (typeof ChickenLittle === 'undefined') {
          console.error("[DEBUG PRICE LOOKUP] API endpoint 'ChickenLittle' is not defined.");
          throw new Error("Application endpoint is missing.");
        }

        const apiUrl = `${ChickenLittle}prices/lookup?locationId=${this.locationId}&brandId=${this.brandId}`;
        console.log(`[DEBUG PRICE LOOKUP] Calling: ${apiUrl}`);

        const response = await axios.get(apiUrl, {
          headers: {
            'Api-Key': this.companyApiKey
          }
        });

        console.log("[DEBUG PRICE LOOKUP] Response:", response.data);

        if (response.data && response.data.found === false) {
          // Price not found
          this.priceLookupError = "Price not available for this location/brand";
          this.showOptionalUnitPrice = false;
          this.unitPriceDetails = null;
        } else if (response.data && response.data.price) {
          // Price found - populate the unit price field
          const priceData = response.data;
          this.unitPriceDetails = {
            id: 'farmdepot-unit-price',
            label: 'Unit Price',
            backendKey: 'unit_price',
            type: 'payment',
            value: parseFloat(priceData.price) || 0,
            required: false,
            placeholder: 'Enter unit price',
            helpText: `Price for location ${this.locationId}, brand ${this.brandId}`,
            unitPrice: parseFloat(priceData.price) || 0
          };
          this.showOptionalUnitPrice = true;
          this.priceLookupError = null;
        }

        this.$nextTick(() => {
          this.updateSummary();
        });

      } catch (error) {
        console.error("[DEBUG PRICE LOOKUP] Error:", error);
        // Handle error response
        if (error.response?.status === 404 || error.response?.data?.found === false) {
          this.priceLookupError = "Price not available for this location/brand";
        } else {
          this.priceLookupError = error.response?.data?.message || error.message || "Failed to lookup price";
        }
        this.showOptionalUnitPrice = false;
        this.unitPriceDetails = null;
      } finally {
        this.isLoadingPrice = false;
        console.log("[DEBUG PRICE LOOKUP] Price lookup complete.");
      }
    },

    // Fetch active locations for multi-brand selection
    async fetchLocations() {
      if (typeof ChickenLittle === 'undefined') {
        console.error("[DEBUG LOCATIONS] API endpoint 'ChickenLittle' is not defined.");
        return;
      }
      try {
        const apiUrl = `${ChickenLittle}api/locations?status=active`;
        console.log(`[DEBUG LOCATIONS] Fetching from: ${apiUrl}`);
        const response = await axios.get(apiUrl, {
          headers: { 'Api-Key': this.companyApiKey }
        });
        console.log("[DEBUG LOCATIONS] Response:", response.data);
        this.locations = Array.isArray(response.data) ? response.data : [];
        // Auto-load brands if a location was previously selected
        if (this.selectedLocationId) {
          this.fetchBrandsForLocation(this.selectedLocationId);
        }
      } catch (error) {
        console.error("[DEBUG LOCATIONS] Error:", error);
        this.locations = [];
      }
    },

    // Fetch prices + brand names + stock for selected location
    async fetchBrandsForLocation(locationId) {
      if (typeof ChickenLittle === 'undefined') {
        console.error("[DEBUG BRANDS] API endpoint 'ChickenLittle' is not defined.");
        return;
      }
      this.isLoadingPrice = true;
      try {
        const apiUrl = `${ChickenLittle}api/prices`;
        console.log(`[DEBUG BRANDS] Fetching prices from: ${apiUrl}`);
        const response = await axios.get(apiUrl, {
          headers: { 'Api-Key': this.companyApiKey }
        });
        console.log("[DEBUG BRANDS] Response count:", Array.isArray(response.data) ? response.data.length : 0);
        const allPrices = Array.isArray(response.data) ? response.data : [];

        // Fetch brand names
        const brandResponse = await axios.get(`${ChickenLittle}api/brands?status=active`, {
          headers: { 'Api-Key': this.companyApiKey }
        });
        const allBrands = Array.isArray(brandResponse.data) ? brandResponse.data : [];
        const brandMap = {};
        allBrands.forEach(b => { brandMap[b.id] = { name: b.name, logo_url: b.logo_url }; });

        // Filter prices by locationId, only include prices whose brand exists and is active.
        // Also hide items the admin has marked as unavailable (isAvailable=0). Treat null as
        // available for backward compat with rows created before the column existed.
        this.availableBrands = allPrices
            .filter(p => p.locationId === locationId
                && brandMap[p.brandId]
                && (p.isAvailable == null || p.isAvailable !== 0))
            .map(p => ({
              ...p,
              brandName: brandMap[p.brandId]?.name || `Brand ${p.brandId}`,
              logoUrl: brandMap[p.brandId]?.logo_url || null,
              stockCount: p.stockCount || 0
            }));

        // Initialize all brands with qty 0. `brandId` is the actual company_brands.id
        // (from the PriceMatrix spread) — required so the cart payload sent to
        // /api/v2/stock/deduct uses the real brand FK, not the PriceMatrix table's PK.
        this.availableBrands.forEach(brand => {
          if (!this.selectedBrands[brand.id]) {
            this.selectedBrands[brand.id] = { id: brand.id, brandId: brand.brandId, name: brand.brandName || brand.name, price: brand.price || 0, quantity: 0, stockCount: brand.stockCount || 0, logoUrl: brand.logoUrl || null, stockWarning: null };
          } else {
            this.selectedBrands[brand.id].stockCount = brand.stockCount || 0;
          }
        });

        // Auto-fill paymentAmountDetails with cart total
        if (this.paymentAmountDetails) {
          this.paymentAmountDetails.value = this.multiBrandCartTotal();
        }
      } catch (error) {
        console.error("[DEBUG BRANDS] Error:", error);
        this.availableBrands = [];
      } finally {
        this.isLoadingPrice = false;
      }
    },

    // Handle location selection change
    onLocationChange(event) {
      const locId = parseInt(event.target.value, 10);
      this.selectedLocationId = locId;
      localStorage.setItem('fd_selectedLocationId', locId);
      this.fetchBrandsForLocation(locId);
    },

    // Toggle brand selection with quantity
    toggleBrand(brandId) {
      if (this.selectedBrands[brandId]) {
        // Deselect
        delete this.selectedBrands[brandId];
      } else {
        // Select with default quantity 1
        const brand = this.availableBrands.find(b => b.id === brandId);
        if (brand) {
          this.selectedBrands[brandId] = {
            id: brand.id,
            brandId: brand.brandId,  // FK to company_brands.id — required for /api/v2/stock/deduct
            name: brand.brandName || brand.name || `Brand ${brandId}`,
            price: brand.price || 0,
            quantity: 1
          };
        }
      }
      this.updateSummary();
    },

    // Update quantity for a selected brand
    updateBrandQuantity(brandId, qty) {
      if (this.selectedBrands[brandId]) {
        const brand = this.availableBrands.find(b => b.id === brandId);
        const maxStock = brand?.stockCount || 0;
        const newQty = Math.max(0, parseInt(qty, 10) || 0);
        if (maxStock > 0 && newQty > maxStock) {
          this.selectedBrands[brandId].stockWarning = `Only ${maxStock} available`;
        } else {
          this.selectedBrands[brandId].stockWarning = null;
        }
        // Store the typed value as-is; warning is shown but value is not capped
        this.selectedBrands[brandId].quantity = newQty;
      }
    },

    // Check stock warnings for all brands — lightweight, no DOM touches
    checkStockWarnings() {
      Object.keys(this.selectedBrands).forEach(brandId => {
        const brand = this.availableBrands.find(b => b.id === parseInt(brandId));
        const rawQty = this.selectedBrands[brandId].quantity;
        const qty = parseInt(rawQty, 10) || 0;
        // Keep the numeric value in sync
        this.selectedBrands[brandId].quantity = qty;
        if (brand && brand.stockCount > 0 && qty > brand.stockCount) {
          this.selectedBrands[brandId].stockWarning = `Only ${brand.stockCount} available`;
        } else {
          this.selectedBrands[brandId].stockWarning = null;
        }
      });
      // Keep payment amount in sync with cart total
      if (this.multiBrandMode && this.paymentAmountDetails) {
        this.paymentAmountDetails.value = this.multiBrandCartTotal();
      }
    },

    // Compute multi-brand cart total for summary
    multiBrandCartTotal() {
      return Object.values(this.selectedBrands).reduce((sum, item) => {
        return sum + (item.price * item.quantity);
      }, 0);
    },

    // Check if any brand has quantity > 0
    hasMultiBrandSelections() {
      return Object.values(this.selectedBrands).some(item => item.quantity > 0);
    },

    // Fetches the form structure and API key from the backend
    async getForm() {
      // Assuming 'ChickenLittle' is a globally defined variable or available elsewhere
      if (typeof ChickenLittle === 'undefined') {
        console.error("[DEBUG FORM] API endpoint 'ChickenLittle' is not defined.");
        Swal.fire({ icon: 'error', title: 'Configuration Error', text: 'Application endpoint is missing.' });
        return;
      }
      if (!this.companyName) {
        console.error("[DEBUG FORM] Cannot fetch form without company alias.");
        return;
      }

      this.loading = true;
      this.paymentAmountDetails = null;
      this.unitPriceDetails = null;
      this.showOptionalUnitPrice = false;
      this.formFields = [];
      this.companyApiKey = null;
      this.logo = this.defaultImageUrl; // Reset logo

      console.log(`[DEBUG FORM] Fetching form and API key for alias: ${this.companyName}`);

      try {
        const apiUrl = `${ChickenLittle}Requirement/getCompanyFormByAlias?alias=${this.companyName}`;
        const { data } = await axios.get(apiUrl);

        // Basic validation of the received data structure
        if (!data || typeof data.form !== 'string' || !data.form || !data.apiKey) {
          console.error("[DEBUG FORM] Invalid data structure received from backend. Expected { form: \"{\\\"form\\\": [...fields...]}\", apiKey: '...' } Got:", data);
          throw new Error("Invalid or incomplete data received from server (Missing form string or API key).");
        }

        this.companyApiKey = data.apiKey;
        console.log(`[DEBUG FORM] Successfully fetched companyApiKey: ${this.companyApiKey}`);

        // Fetch company features to determine if Farm Depot (price matrix) is available
        await this.fetchCompanyFeatures();

        // After features are loaded, set multiBrandMode.
        // CONSTRAINT (R7 + T8): when Farm Depot is on, customer must NEVER see legacy form,
        // even if deep-link URL params (?locationId=&brandId=) are present.
        // URL params become PRE-SELECTION inside the multi-brand UI, not a bypass.
        this.multiBrandMode = this.usesPriceMatrix;
        if (this.usesPriceMatrix) {
          if (this.locationId) this.selectedLocationId = this.locationId;
          if (this.brandId) this.preselectedBrandId = this.brandId;
        }

        // Parse the nested JSON string containing the form fields
        let loadedFields = [];
        try {
          const parsedData = JSON.parse(data.form);
          console.log("[DEBUG FORM] Parsed outer form data:", parsedData);
          if (parsedData && Array.isArray(parsedData.form)) {
            loadedFields = parsedData.form;
          } else {
            console.error("[DEBUG FORM] Parsed data does not contain a 'form' array:", parsedData);
            throw new Error("Form data structure is incorrect after parsing. Expected nested 'form' array.");
          }
        } catch (parseError) {
          console.error("[DEBUG FORM] Error parsing 'form' JSON string from backend:", parseError, "Raw form string:", data.form);
          throw new Error("Could not parse the form data received from the server.");
        }

        console.log(`[DEBUG FORM] Received and extracted ${loadedFields.length} fields from API.`);

        // Process and categorize fields
        const customFields = [];
        loadedFields.forEach((field, idx) => {
          const cleanedField = this.cleanAndInitializeField(field); // Clean and set defaults
          if (cleanedField === null) {
            console.warn(`[DEBUG FORM] Field at index ${idx} failed cleaning and was skipped.`);
            return; // Skip invalid fields
          }

          // Categorize based on backendKey
          if (cleanedField.backendKey === 'amount') {
            this.paymentAmountDetails = cleanedField;
          } else if (cleanedField.backendKey === 'unit_price') {
            this.unitPriceDetails = cleanedField;
            this.showOptionalUnitPrice = true;
          } else {
            customFields.push(cleanedField);
          }
        });
        this.formFields = customFields;

        // Check if any field actually defines an amount to be paid
        const hasAmountDefiningField = this.paymentAmountDetails || this.formFields.some(f => f.SetUnit || f.isInstallmentPayment || f.type === 'payment');
        if (!hasAmountDefiningField) {
          console.warn("[DEBUG FORM] No primary amount field ('amount') or custom payment-defining field found. Payment might be 0.");
          // Optionally show a user-facing warning here if needed
        }

        this.getLogo(); // Fetch logo now that we have the API key

        // If location_id and brand_id params are present, lookup price from Price Matrix
        if (this.locationId && this.brandId) {
          this.lookupPriceByLocationBrand();
        } else if (this.multiBrandMode) {
          // No URL params - fetch locations for multi-brand selection
          this.fetchLocations();
        }

      } catch (error) {
        console.error("[DEBUG FORM] Error during getForm execution:", error);
        const message = error.response?.data?.message || error.message || "Could not load the payment form details.";
        // Avoid showing generic parse errors to the user
        if (!error.message.includes("Could not parse") && !error.message.includes("Form data structure is incorrect")) {
          await Swal.fire({ icon: 'error', title: 'Load Failed', text: message });
        }
        // Reset state on error
        this.paymentAmountDetails = null; this.unitPriceDetails = null; this.showOptionalUnitPrice = false; this.formFields = [];
        this.companyApiKey = null;
        this.logo = this.defaultImageUrl;
      } finally {
        this.loading = false;
        console.log("[DEBUG FORM] Form loading finished.");
        this.$nextTick(() => { /* Optional: Force UI update if needed, though usually automatic */ });
      }
    },

    // Cleans raw field data from backend and initializes default values and structures
    cleanAndInitializeField(field) {
      try {
        // Base structure with defaults for all possible properties
        const base = {
          label: '', type: 'text', value: '', required: false, placeholder: '', helpText: '',
          options: [], conditionalLogic: { enabled: false, sourceField: '', backendKey: field.backendKey || null, operator: 'equals', value: '' },
          quantity: null, // Default quantity to null
          unitPrice: undefined, unitLabel: '', SetUnit: false,
          isInstallmentPayment: false, installmentDetails: null,
          fileObject: null, displayValue: '', id: field.id || `field-${Date.now()}-${Math.random()}` // Generate unique ID if missing
        };

        // Merge backend field data onto the base structure
        const cleaned = { ...base, ...field };

        // Standardize type 'number' to 'payment' for consistency
        if (cleaned.type === 'number') cleaned.type = 'payment';

        // Ensure boolean flags are actually booleans
        cleaned.SetUnit = !!cleaned.SetUnit;
        cleaned.isInstallmentPayment = !!cleaned.isInstallmentPayment;

        // --- Logic for SetUnit fields ---
        if (cleaned.SetUnit) {
          // Handle potential 'amount' key used for unit price historically
          if (cleaned.unitPrice === undefined && cleaned.amount !== undefined) cleaned.unitPrice = cleaned.amount;
          delete cleaned.amount; // Remove the old key
          cleaned.unitLabel = cleaned.unitLabel || ''; // Default unit label
          cleaned.unitPrice = parseFloat(cleaned.unitPrice) || 0; // Ensure unit price is a number

          // Parse quantity, keep null if originally null/undefined/empty string
          const rawQuantity = cleaned.quantity;
          cleaned.quantity = (rawQuantity !== undefined && rawQuantity !== null && rawQuantity !== '') ? parseInt(rawQuantity, 10) : null;
          // Ensure quantity is not negative if provided and parsed
          if (cleaned.quantity !== null && (isNaN(cleaned.quantity) || cleaned.quantity < 0)) cleaned.quantity = 0;


          // --- Nested logic for SetUnit + Installment fields ---
          if (cleaned.isInstallmentPayment) {
            const paidNowRaw = cleaned.installmentDetails?.amountPaidNow;
            // Initialize amountPaidNow to null (user must enter or it defaults to full payment)
            let initialPaidNow = null;
            if (paidNowRaw !== undefined && paidNowRaw !== null && paidNowRaw !== '') {
              const parsed = parseFloat(paidNowRaw);
              // Only set if it's a valid positive number (allow 0 to be entered later)
              if (!isNaN(parsed) && parsed > 0) initialPaidNow = parsed;
            }
            cleaned.installmentDetails = { amountPaidNow: initialPaidNow };
            // Calculate initial total value based on unit price and quantity (used for validation/max)
            cleaned.value = (cleaned.unitPrice || 0) * Math.max(0, cleaned.quantity || 0);
          } else {
            // If not installment, remove installment details
            delete cleaned.installmentDetails;
          }
        }
        // --- Logic for Installment fields (NOT SetUnit) ---
        else if (cleaned.isInstallmentPayment) {
          const paidNowRaw = cleaned.installmentDetails?.amountPaidNow;
          // Initialize amountPaidNow to null (user must enter amount to pay)
          let initialPaidNow = null;
          if (paidNowRaw !== undefined && paidNowRaw !== null && paidNowRaw !== '') {
            const parsed = parseFloat(paidNowRaw);
            // Only set if it's a valid positive number
            if (!isNaN(parsed) && parsed > 0) initialPaidNow = parsed;
          }
          cleaned.installmentDetails = { amountPaidNow: initialPaidNow };
          // Ensure the main value (total for this item) is a number
          cleaned.value = parseFloat(cleaned.value) || 0;
          // Remove SetUnit related properties
          delete cleaned.unitPrice; delete cleaned.unitLabel; delete cleaned.quantity;
        }
        // --- Logic for fields that are neither SetUnit nor Installment ---
        else {
          // Remove properties related to SetUnit and Installments
          delete cleaned.unitPrice; delete cleaned.unitLabel; delete cleaned.quantity; delete cleaned.installmentDetails;
        }

        // --- Standardize 'options' for relevant field types ---
        if (['select', 'radio', 'checkbox-group'].includes(cleaned.type)) {
          cleaned.options = Array.isArray(cleaned.options) ? cleaned.options : (cleaned.options ? [String(cleaned.options)] : []);
          // Provide default options if empty for select/radio to avoid errors
          if (cleaned.options.length === 0 && ['select', 'radio'].includes(cleaned.type)) cleaned.options = ['Option 1'];
        } else {
          // Remove options array if not applicable
          delete cleaned.options;
        }

        // --- Initialize value structure for specific types ---
        // Checkbox-group: value should be an object { option: boolean }
        if (cleaned.type === 'checkbox-group') {
          const opts = cleaned.options || [];
          const currentVal = (typeof cleaned.value === 'object' && cleaned.value !== null && !Array.isArray(cleaned.value)) ? cleaned.value : {};
          cleaned.value = opts.reduce((acc, opt) => { acc[opt] = !!currentVal[opt]; return acc; }, {});
        } else if (typeof cleaned.value === 'object' && cleaned.value !== null && !Array.isArray(cleaned.value)) {
          // Reset value if it's an object but not for checkbox-group (e.g., leftover installmentDetails)
          cleaned.value = '';
        }

        // Checkbox: value should be a boolean
        if (cleaned.type === 'checkbox') {
          cleaned.value = !!cleaned.value;
          // Ensure options array exists for label lookup, provide default
          if (!cleaned.options || cleaned.options.length === 0) cleaned.options = ['Confirm'];
        }

        // File: Reset file-related properties
        if (cleaned.type === 'file') {
          cleaned.displayValue = ''; cleaned.fileObject = null; cleaned.value = ''; // Value might store filename later
        }

        // Date: Format value to YYYY-MM-DD if valid
        if (cleaned.type === 'date' && cleaned.value) {
          try {
            const d = new Date(cleaned.value);
            cleaned.value = !isNaN(d.getTime()) ? d.toISOString().split('T')[0] : '';
          } catch (e) { cleaned.value = ''; }
        }

        // Ensure placeholder and helpText are strings
        cleaned.placeholder = String(field.placeholder || '');
        cleaned.helpText = String(field.helpText || '');

        // Ensure conditional logic structure exists and 'enabled' is boolean
        cleaned.conditionalLogic = cleaned.conditionalLogic || { enabled: false, sourceField: '', operator: '', value: '' };
        cleaned.conditionalLogic.enabled = !!cleaned.conditionalLogic.enabled;

        return cleaned; // Return the processed field object
      } catch (e) {
        console.error("[DEBUG] Error cleaning field:", field, e);
        return null; // Return null if cleaning fails
      }
    },

    // Method to trigger summary recalculation (often called on input events)
    updateSummary() {
      // This method primarily exists to be called by @input.
      // Vue's reactivity usually handles the updates automatically when
      // computed properties depend on data properties (like formFields, paymentAmountDetails).
      // A $nextTick can ensure DOM updates related to conditional rendering happen
      // before any potential manual checks, but it's often not strictly necessary.
      this.$nextTick(() => {
        // console.log("[DEBUG] Summary updated triggered.");
        // this.$forceUpdate(); // Avoid using $forceUpdate unless absolutely necessary.
      });
    },

    // Handles changes to the 'Amount Paid Now' input for installment fields
    handleAmountPaidNowChange(field) {
      if (!field.isInstallmentPayment || !field.installmentDetails) return;

      const paidNowInput = field.installmentDetails.amountPaidNow; // This might be null if input is cleared

      // If the input is not null and is a valid number, clamp it
      if (paidNowInput !== null && typeof paidNowInput === 'number' && !isNaN(paidNowInput)) {
        let itemTotal = 0;
        // Calculate the total value for this specific item
        if (field.SetUnit) {
          itemTotal = (parseFloat(field.unitPrice) || 0) * Math.max(0, parseInt(field.quantity, 10) || 0);
        } else { // Installment, but not SetUnit
          itemTotal = parseFloat(field.value) || 0; // The 'value' holds the total amount
        }
        // Clamp the entered amount between 0 and the item's total value
        field.installmentDetails.amountPaidNow = Math.max(0, Math.min(paidNowInput, itemTotal));
      }
      // If input is null or not a number (e.g., user cleared the input), keep it as is (null) in the model.
      // The computed properties (totalAmountPaidNow, etc.) and validation logic will handle the null value appropriately.

      this.updateSummary(); // Trigger summary recalculation
    },

    // Handles changes to fields that affect installment totals (e.g., quantity for SetUnit, or total value for non-SetUnit)
    handleInstallmentTotalChange(field) {
      // Update the field's 'value' property if it's a SetUnit field, as 'value' represents the item total here.
      if (field.SetUnit) {
        const currentQuantity = field.quantity; // Could be null if input is empty/invalid temporarily
        field.value = (parseFloat(field.unitPrice) || 0) * Math.max(0, parseInt(currentQuantity, 10) || 0);
      }

      // If it's an installment field (either SetUnit or not), potentially adjust 'amountPaidNow'
      // if it now exceeds the new item total.
      if (field.isInstallmentPayment && field.installmentDetails) {
        let itemTotal = 0;
        if (field.SetUnit) {
          itemTotal = field.value; // Use the value just calculated above
        } else { // Installment, but not SetUnit
          itemTotal = parseFloat(field.value) || 0; // Use the direct value input which is the total
        }

        const paidNow = field.installmentDetails.amountPaidNow;
        // Only adjust if paidNow is currently a number and exceeds the new item total
        if (typeof paidNow === 'number' && !isNaN(paidNow) && itemTotal < paidNow) {
          // Reset 'amountPaidNow' to the new total, or null if the total is now 0 or less
          field.installmentDetails.amountPaidNow = itemTotal > 0 ? itemTotal : null;
        }
      }
      this.updateSummary(); // Trigger summary recalculation
    },

    // Handles file selection for file input fields
    handleFileUpload(event, field) {
      const file = event.target.files[0];
      if (file) {
        field.fileObject = file; // Store the File object
        field.displayValue = file.name; // Store the filename for display
        field.value = file.name; // Store filename as the field's 'value' for submission/summary
      } else {
        // Reset if no file is selected or selection is cancelled
        field.fileObject = null;
        field.displayValue = '';
        field.value = '';
      }
      this.updateSummary(); // Trigger summary update
    },

    // Prepares the data payload for submission to the backend API
    prepareFormData() {
      const payloadData = {};
      // Generate a unique local ID for the transaction attempt
      payloadData.localid = typeof generateUUID === 'function'
          ? generateUUID() // Use global UUID function if available
          : `local-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`; // Fallback

      // Add the company API key (CRITICAL)
      payloadData.apikey = this.companyApiKey;
      console.log("[DEBUG PAYLOAD] Using API Key:", payloadData.apikey);
      if (!payloadData.apikey) {
        console.error("[DEBUG PAYLOAD] CRITICAL ERROR: API Key is missing.");
        Swal.fire({ icon: 'error', title: 'Configuration Error', text: 'Cannot proceed: Company API Key is missing.' });
        return null; // Indicate failure
      }

      // Basic transaction description
      payloadData.description = `Payment via form for ${this.companyName || 'service'}`;

      // Set the main transaction amount to the calculated total being paid now
      const totalAmountNum = parseFloat(this.totalAmountPaidNow);
      payloadData.totalAmount = isNaN(totalAmountNum) ? 0 : totalAmountNum;

      // No custom Sucess_Url — Kazang will use the merchant's default configured in
      // KazangAccountDetials.vue. Stock deduction is now triggered manually by staff
      // from the Admin Portal "Deduct Stock" button in TransactionManager.vue.

      // --- Prepare additionalFields ---
      const additionalInfo = {};

      // Include multi-brand cart items if in multi-brand mode. The cart is
      // NO LONGER stuffed into additionalInfo — the remote payment gateway
      // flattens nested objects and drops arrays, so the Admin Portal would
      // never see the cart from there. Instead we surface cartItems +
      // locationId + locationName on the payload and POST them to the new
      // /api/v2/stock/order-cart endpoint before createOrder, so the
      // server-side OrderCartCache holds the cart keyed by localId and
      // Reports.getTransactionHistory() can inject it into the Admin row.
      if (this.multiBrandMode && this.hasMultiBrandSelections()) {
        const selectedLocation = this.locations.find(l => l.id === this.selectedLocationId);
        const cartItems = Object.entries(this.selectedBrands)
            .filter(([_, item]) => item.quantity > 0)
            .map(([_, item]) => ({
              // item.brandId is the real company_brands.id (preserved on init).
              // The key of selectedBrands is the PriceMatrix.id, which is the wrong
              // value to send — the deduction endpoint filters company_price_matrix
              // by brand_id (the brand FK), not by the price-matrix PK.
              brandId: item.brandId,
              name: item.name,
              price: item.price,
              quantity: item.quantity,
              lineTotal: item.price * item.quantity
            }));
        payloadData.cartItems = cartItems;
        payloadData.locationId = this.selectedLocationId;
        payloadData.locationName = selectedLocation?.name || null;
      }

      this.formFields.forEach(field => {
        // Only include fields that are currently visible (respecting conditional logic)
        if (this.shouldShowField(field)) {
          // Determine the key to use for this field in the JSON payload
          let key = field.backendKey;
          // Sanitize or generate a key if backendKey is missing, invalid, or reserved
          if (!key || key === 'amount' || key === 'unit_price' || /\s/.test(key) || !/^[a-zA-Z0-9_]+$/.test(key)) {
            key = (field.label || `field_${field.id}`) // Use label or generated ID as base
                .replace(/\s+/g, '_') // Replace spaces with underscores
                .replace(/[^a-zA-Z0-9_]/g, '') // Remove invalid characters
                .substring(0, 50); // Limit length
          }

          if (key) {
            // --- Handle SetUnit fields ---
            if (field.SetUnit) {
              const quantity = Math.max(0, parseInt(field.quantity, 10) || 0);
              // Only include if quantity is greater than 0
              if (quantity > 0) {
                // Add quantity field
                const quantityKey = `${key}_quantity`;
                additionalInfo[quantityKey] = quantity;

                // Optionally send unit price if needed by backend (uncomment if required)
                // const unitPriceKey = `${key}_unit_price`;
                // additionalInfo[unitPriceKey] = field.unitPrice || 0;

                // --- Handle SetUnit + Installment fields ---
                if (field.isInstallmentPayment) {
                  const itemTotal = (field.unitPrice || 0) * quantity;
                  const amountEntered = field.installmentDetails?.amountPaidNow;
                  // Determine effective amount paid: full if blank/0/null, otherwise the entered (clamped) amount
                  const effectivePaidNow = (amountEntered == null || isNaN(amountEntered) || amountEntered <= 0)
                      ? itemTotal // Paying full by default
                      : Math.max(0, Math.min(amountEntered, itemTotal)); // Paying specified amount

                  // Add paid now and remaining due for this item
                  const paidNowKey = `${key}_paid_now`;
                  additionalInfo[paidNowKey] = effectivePaidNow;
                  const remainingDueKey = `${key}_remaining_due`;
                  additionalInfo[remainingDueKey] = Math.max(0, itemTotal - effectivePaidNow);
                }
              }
            }
            // --- Handle Installment fields (NOT SetUnit) ---
            else if (field.isInstallmentPayment) {
              const itemTotal = parseFloat(field.value) || 0; // Total is stored in 'value'
              const amountEntered = field.installmentDetails?.amountPaidNow;
              // Determine effective amount paid: 0 if blank/0/null, otherwise the entered (clamped) amount
              const effectivePaidNow = (amountEntered != null && !isNaN(amountEntered) && amountEntered > 0)
                  ? Math.max(0, Math.min(amountEntered, itemTotal))
                  : 0; // Paying 0 by default if not specified

              // Only include if the item total is greater than 0
              if (itemTotal > 0) {
                // Add total, paid now, and remaining due for this item
                const totalKey = `${key}_total`; // Send the item's total value
                additionalInfo[totalKey] = itemTotal;
                const paidNowKey = `${key}_paid_now`;
                additionalInfo[paidNowKey] = effectivePaidNow;
                const remainingDueKey = `${key}_remaining_due`;
                additionalInfo[remainingDueKey] = Math.max(0, itemTotal - effectivePaidNow);
              }
            }
                // --- Handle other standard fields ---
            // Exclude simple 'payment' type fields as they contribute directly to the main totalAmount
            else if (field.type !== 'payment') {
              const payloadValue = this.getPayloadValue(field); // Get formatted value
              // Include if the value is not null/undefined and not an empty array
              if (payloadValue !== null && payloadValue !== undefined && !(Array.isArray(payloadValue) && payloadValue.length === 0)) {
                additionalInfo[key] = payloadValue;
              }
            }
          } else {
            // Log a warning if a valid key couldn't be generated
            console.warn(`[DEBUG PAYLOAD] Could not generate valid key for field:`, field);
          }
        }
      });
      // Stringify the collected additional info
      payloadData.additionalFields = JSON.stringify(additionalInfo);

      console.log("[DEBUG PAYLOAD] Payload prepared:", JSON.stringify(payloadData, null, 2));
      return payloadData; // Return the complete payload object
    },


    // Handles the form submission process, including validation and API call
    async submitForm() {
      // --- Start: Validation Logic ---
      let firstErrorFieldLabel = null; // Label of the first field with an error
      let firstErrorElementId = null; // ID of the element to focus for the error
      // Helper function to record the first error encountered
      const setFirstError = (label, elementId) => {
        if (!firstErrorFieldLabel) {
          firstErrorFieldLabel = label;
          firstErrorElementId = elementId;
        }
      };

      // Regular expression for basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      // Regular expression for basic phone number validation (allows digits, +, (), -, spaces)
      const phoneRegex = /^[+]?[\s./0-9]*[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;


      // 1. Validate primary payment amount (if it exists and is required)
      if (this.paymentAmountDetails?.required && (this.paymentAmountDetails.value == null || this.paymentAmountDetails.value < 0)) {
        setFirstError(this.paymentAmountDetails.label, this.paymentAmountDetails.id || 'default-amount');
      }
      // 2. Validate optional unit price (if shown and required)
      if (!firstErrorFieldLabel && this.showOptionalUnitPrice && this.unitPriceDetails?.required && (this.unitPriceDetails.value == null || this.unitPriceDetails.value < 0)) {
        setFirstError(this.unitPriceDetails.label, this.unitPriceDetails.id || 'optional-unit-price');
      }
      // 3. Validate multi-brand stock limits — collect ALL over-stock items and report them
      // together so the customer can fix them in one pass instead of one-at-a-time.
      if (!firstErrorFieldLabel && this.multiBrandMode) {
        const overStock = [];
        for (const brandId in this.selectedBrands) {
          const item = this.selectedBrands[brandId];
          if (item.quantity > 0) {
            const brand = this.availableBrands.find(b => b.id === parseInt(brandId));
            if (brand && brand.stockCount > 0 && item.quantity > brand.stockCount) {
              overStock.push(brand);
            }
          }
        }
        if (overStock.length > 0) {
          const lines = overStock.map(b => {
            const name = b.brandName || b.name;
            const unit = b.stockCount === 1 ? 'unit' : 'units';
            return `<b>${name}</b> — only ${b.stockCount} ${unit} available.`;
          });
          Swal.fire({
            title: overStock.length === 1 ? 'Stock Limit Exceeded' : 'Stock Limits Exceeded',
            html: lines.join('<br>'),
            icon: 'warning',
            confirmButtonText: 'OK'
          });
          return;
        }
      }
      // 4. Validate custom fields
      if (!firstErrorFieldLabel) {
        for (let i = 0; i < this.formFields.length; i++) {
          const field = this.formFields[i];
          if (!this.shouldShowField(field)) continue; // Skip hidden fields

          let isMissingOrInvalid = false; // Changed from isMissing to reflect format validation too
          const rawValue = this.getRawValue(field);
          const elementId = field.id || `field-input-${i}`; // Base ID for focusing

          // --- Validation for SetUnit fields ---
          if (field.SetUnit) {
            const quantity = parseInt(field.quantity, 10);
            const quantityElementId = `quantity-${i}`;
            // Check if required and quantity is missing or not positive
            if (field.required && (isNaN(quantity) || quantity <= 0)) {
              setFirstError(field.label + " (Quantity is required and must be > 0)", quantityElementId);
              break;
            }
            // Check if not required, but quantity is entered and invalid (negative or zero when price > 0)
            else if (!field.required && field.quantity !== null && (isNaN(quantity) || quantity <= 0) && (parseFloat(field.unitPrice) || 0) > 0) {
              setFirstError(field.label + " (Quantity must be a positive number if specified)", quantityElementId);
              break;
            }
          }
          // --- Validation for other required fields (and specific format checks) ---
          else {
            if (field.required) {
              if (field.type === 'checkbox') isMissingOrInvalid = (rawValue !== true);
              else if (field.type === 'checkbox-group') isMissingOrInvalid = !Object.values(field.value || {}).some(v => v === true);
              else if (field.type === 'file') isMissingOrInvalid = !field.fileObject;
              // For non-SetUnit installments, the total value input is required if the field itself is required
              else if (field.isInstallmentPayment) {
                isMissingOrInvalid = (rawValue === null || rawValue === '' || parseFloat(rawValue) <= 0);
                if (isMissingOrInvalid) {
                  setFirstError(field.label + " (Total amount is required and must be > 0)", `installment-total-${i}`); break;
                }
              }
              // Default check for text, email, tel, textarea, select, radio, date, simple payment (emptiness)
              else isMissingOrInvalid = rawValue === null || String(rawValue).trim() === '';

              if (isMissingOrInvalid) {
                setFirstError(field.label, elementId); break;
              }
            }

            // Additional format validation for specific types, even if not required but has a value
            if (field.type === 'email' && rawValue && String(rawValue).trim() !== '' && !emailRegex.test(String(rawValue))) {
              setFirstError(field.label + " (Invalid email format)", elementId); break;
            }
            if (field.type === 'tel' && rawValue && String(rawValue).trim() !== '' && !phoneRegex.test(String(rawValue))) {
              setFirstError(field.label + " (Invalid phone number format)", elementId); break;
            }
          }


          // --- Additional validation specific to installment amounts ---
          if (field.isInstallmentPayment) {
            const paidNow = field.installmentDetails?.amountPaidNow; // Can be null
            let itemTotal = 0;
            // Calculate item total based on type
            if(field.SetUnit) itemTotal = (parseFloat(field.unitPrice) || 0) * Math.max(0, parseInt(field.quantity, 10) || 0);
            else itemTotal = parseFloat(field.value) || 0; // Total is in 'value' for non-SetUnit

            // Check if 'amountPaidNow' is entered, valid, but invalid (negative or exceeds item total)
            if (paidNow !== null && typeof paidNow === 'number' && !isNaN(paidNow) && (paidNow < 0 || paidNow > itemTotal)) {
              setFirstError(field.label + " (Invalid amount to pay now)", `installment-paid-${i}`); break;
            }
          }
        } // End for loop through formFields
      }
      // 4. Validate total amount being paid
      if (!firstErrorFieldLabel && this.totalAmountPaidNow <= 0) {
        // Allow 0 payment ONLY IF there's a remaining balance (implies setting up future payments)
        if (this.totalRemainingDue <= 0) {
          setFirstError("Total Amount to Pay", null); // No specific element to focus
          Swal.fire({ icon: 'warning', title: 'Invalid Amount', text: `The total amount to pay cannot be zero or less unless setting up future installments.` });
          return; // Stop submission
        }
      }

      // 5. Validate multi-brand selections
      if (!firstErrorFieldLabel && this.multiBrandMode && !this.hasMultiBrandSelections()) {
        Swal.fire({ icon: 'warning', title: 'No Items Selected', text: 'Please select at least one brand and enter a quantity.' });
        return;
      }

      // --- Handle Validation Errors ---
      if (firstErrorFieldLabel) {
        // Show error message to the user
        await Swal.fire({
          icon: 'warning', title: 'Missing or Invalid Information', text: `Please check the field: "${firstErrorFieldLabel}"`
        });
        // Scroll to and focus the problematic element if possible
        if (firstErrorElementId) {
          const errorElement = document.getElementById(firstErrorElementId);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            try { errorElement.focus({ preventScroll: true }); } // Try to focus
            catch (e) { console.warn("Could not focus error element:", e); }
          }
        }
        return; // Stop submission process
      }
      // --- End: Validation Logic ---

      // --- Start Submission ---
      this.isSubmitting = true; // Disable button, show spinner

      try {
        const payload = this.prepareFormData(); // Prepare the data object
        if (!payload) {
          // prepareFormData already showed an error if payload is null (missing API key)
          throw new Error("Payload preparation failed. Check console for details.");
        }

        // Farm Depot: persist the cart server-side BEFORE createOrder so the
        // server's OrderCartCache has it keyed by localId. The cache survives
        // the gateway's additionalFields flatten/drop, and Reports.injectCart()
        // reads from it to surface cart + locationName on the Admin Portal row
        // (works for both pending and approved filters).
        // Non-fatal: if this POST fails the payment still goes through; the
        // Admin Portal just won't have the cart to display / deduct from.
        if (Array.isArray(payload.cartItems) && payload.cartItems.length > 0) {
          try {
            const res = await fetch(`${ChickenLittle}api/v2/stock/order-cart`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Api-Key': this.companyApiKey
              },
              body: JSON.stringify({
                localId: payload.localid,
                companyId: this.companyApiKey,
                locationId: payload.locationId,
                locationName: payload.locationName,
                cart: payload.cartItems
              })
            });
            if (!res.ok) {
              console.warn(`[FD] order-cart POST returned ${res.status}; cart will not appear in Admin Portal`);
            }
          } catch (e) {
            console.warn('[FD] Failed to persist order cart server-side; cart will not appear in Admin Portal', e);
          }
        }

        // --- API Call (using Vuex example) ---
        // Replace 'createTransaction' with your actual Vuex action name or direct API call logic
        console.log("[DEBUG] Dispatching createTransaction with payload:", payload);
        // Ensure your Vuex store is correctly configured if using mapState/dispatch
        const transactionUrl = await this.$store.dispatch('createTransaction', payload);
        console.log("[DEBUG] Received transaction URL:", transactionUrl);

        if (!transactionUrl || typeof transactionUrl !== 'string') {
          throw new Error('Transaction could not be initiated. No valid redirect URL received from the server.');
        }
        // --- End API Call ---

        // --- Redirect on Success ---
        console.log(`[DEBUG] Redirecting to payment gateway: ${transactionUrl}`);
        // No localStorage save — stock deduction is now triggered by the Admin Portal
        // "Deduct Stock" button after staff confirms payment. The cart data travels
        // via the server-side OrderCartCache (POSTed to /api/v2/stock/order-cart
        // above, just before createOrder) so it survives the gateway flatten/drop.

        await Swal.fire({
          icon: 'success', title: 'Redirecting to Payment', text: 'You will be redirected shortly.',
          timer: 3000, // Show message briefly before redirect
          showConfirmButton: false,
          allowOutsideClick: false, // Prevent closing while waiting
          didOpen: () => { Swal.showLoading(); } // Show loading indicator in the modal
        });
        window.location.href = transactionUrl; // Perform the redirect

      } catch (error) {
        // --- Handle Submission Errors ---
        console.error("[DEBUG] Submission Error:", error);
        // Extract error details if available from API response
        const apiError = error.response?.data?.error || 'Transaction Error';
        const apiMessage = error.response?.data?.message || error.message || "Could not process the payment request. Please try again.";
        // Show error to the user
        Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: `Error: ${apiError}. ${apiMessage}`,
          target: '#swal-container' // Target specific container if needed
        });
        this.isSubmitting = false; // Re-enable button on error
      }
      // Note: No 'finally' block to reset isSubmitting, as successful submission redirects away.
    },

    // Formats a number as ZMW currency
    formatCurrency(value) {
      const number = Number(value);
      // Use Intl.NumberFormat for locale-aware currency formatting
      return new Intl.NumberFormat('en-ZM', { style: 'currency', currency: 'ZMW' })
          .format(isNaN(number) ? 0 : number); // Format 0 if value is not a valid number
    },

    // Resets the form by re-fetching the initial data from the backend
    resetForm() {
      console.log("[DEBUG] Resetting form...");
      this.getForm(); // Calls the method that fetches and initializes form data
    },

    // Determines if a field should be shown based on conditional logic rules
    shouldShowField(field) {
      // Always show if conditional logic is not enabled or not fully configured
      if (!field.conditionalLogic?.enabled || !field.conditionalLogic.sourceField || !field.conditionalLogic.operator) {
        return true;
      }

      const { sourceField: sourceFieldId, operator, value: targetValue } = field.conditionalLogic;
      let sourceValue;
      let sourceFieldFound = false;

      // 1. Check if the source is the primary payment amount field
      if (this.paymentAmountDetails && (sourceFieldId === this.paymentAmountDetails.id || sourceFieldId === this.paymentAmountDetails.backendKey)) {
        sourceValue = this.paymentAmountDetails.value; sourceFieldFound = true;
      }
      // 2. Check if the source is the optional unit price field
      else if (this.unitPriceDetails && (sourceFieldId === this.unitPriceDetails.id || sourceFieldId === this.unitPriceDetails.backendKey)) {
        sourceValue = this.unitPriceDetails.value; sourceFieldFound = true;
      }
      // 3. Check if the source is one of the custom fields
      else {
        const sourceField = this.formFields.find(f => f.id === sourceFieldId || f.backendKey === sourceFieldId);
        if (sourceField) {
          sourceValue = this.getRawValue(sourceField); // Use raw value for comparison logic
          sourceFieldFound = true;
        }
      }

      // If the source field couldn't be found, log a warning and default to showing the field
      if (!sourceFieldFound) {
        console.warn(`[DEBUG] Conditional logic source field '${sourceFieldId}' not found for field '${field.label}'. Field will be shown.`);
        return true;
      }

      // Evaluate the condition using the found source value
      return this.evaluateCondition(sourceValue, operator, targetValue);
    },

    // Evaluates a single condition (e.g., 'equals', 'greater_than')
    evaluateCondition(sourceValue, operator, targetValue) {
      // Prepare values for different comparison types
      const numSrc = parseFloat(sourceValue); const numTgt = parseFloat(targetValue);
      const strSrc = (sourceValue ?? '').toString().toLowerCase().trim(); const strTgt = (targetValue ?? '').toString().toLowerCase().trim();
      // Determine boolean representation of source value
      let boolSrc;
      if (typeof sourceValue === 'boolean') boolSrc = sourceValue;
      else boolSrc = ['true', 'yes', 'on', '1'].includes(strSrc); // Common true values
      // Determine boolean representation of target value
      const boolTgt = ['true', 'yes', 'on', '1'].includes(strTgt);

      // Perform comparison based on the operator
      switch (operator) {
        case 'equals':
          // Special handling for boolean comparison vs string comparison
          return (typeof sourceValue === 'boolean') ? (boolSrc === boolTgt) : (strSrc === strTgt);
        case 'not_equals':
          return (typeof sourceValue === 'boolean') ? (boolSrc !== boolTgt) : (strSrc !== strTgt);
        case 'contains':
          // Check if source string includes target string (case-insensitive)
          return targetValue != null && strSrc.includes(strTgt);
        case 'greater_than':
          // Numeric comparison
          return !isNaN(numSrc) && !isNaN(numTgt) && numSrc > numTgt;
        case 'less_than':
          // Numeric comparison
          return !isNaN(numSrc) && !isNaN(numTgt) && numSrc < numTgt;
        case 'is_empty':
          // Check various forms of "empty"
          if (sourceValue == null) return true; // null or undefined
          if (typeof sourceValue === 'string' && strSrc === '') return true; // Empty string
          if (Array.isArray(sourceValue) && sourceValue.length === 0) return true; // Empty array
          // Checkbox group: empty if no options are selected (all values are false)
          if (field.type === 'checkbox-group' && typeof sourceValue === 'object' && !Array.isArray(sourceValue) && sourceValue !== null && !Object.values(sourceValue).some(v => v === true)) return true;
          // Checkbox: empty if false
          if (field.type === 'checkbox' && typeof sourceValue === 'boolean' && sourceValue === false) return true;
          // Consider quantity 0 or null as empty for SetUnit fields in conditional logic
          if (field.SetUnit && (sourceValue === null || sourceValue === 0 || isNaN(parseInt(sourceValue, 10)))) return true;
          // Consider numeric 0 or NaN as empty (use cautiously)
          // if (typeof sourceValue === 'number' && (isNaN(sourceValue) || sourceValue === 0)) return true;
          return false; // Otherwise, not empty
        case 'is_not_empty':
          // Simply the inverse of 'is_empty'
          return !this.evaluateCondition(sourceValue, 'is_empty', targetValue);
        default:
          // Log unknown operators and default to true (show field)
          console.warn(`[DEBUG] Unknown conditional operator: ${operator}.`);
          return true;
      }
    },

    // Gets the raw, unprocessed value of a field, suitable for conditional logic or internal calculations
    getRawValue(field) {
      if (!field) return undefined; // Return undefined if field is invalid
      // For file inputs, the raw value might be considered the file object or its name
      if (field.type === 'file') return field.fileObject ? field.fileObject.name : null;
      // For SetUnit fields, the primary "raw value" is the quantity
      if (field.SetUnit) return field.quantity; // Return quantity directly (can be null)
      // For all other fields, return the standard 'value' property
      return field.value;
    },

    // Gets the value formatted specifically for the 'additionalFields' JSON payload
    getPayloadValue(field) {
      if (!field) return null; // Return null if field is invalid

      // Use the standard 'value' property for most fields when preparing payload
      // (SetUnit and Installment logic is handled directly in prepareFormData)
      const rawValue = field.value;

      // Handle null, undefined, or empty string values
      if (rawValue === null || rawValue === undefined) return field.type === 'checkbox' ? false : null; // Checkbox defaults to false if null/undefined
      if (rawValue === '') return field.type === 'checkbox' ? false : null; // Treat empty string same way

      // Format based on field type
      switch (field.type) {
        case 'checkbox':
          return !!rawValue; // Ensure boolean true/false
        case 'checkbox-group':
          // Filter selected options and return array of keys, or null if none selected
          const selected = Object.entries(rawValue || {}).filter(([, v]) => v === true).map(([k]) => k);
          return selected.length > 0 ? selected : null;
        case 'file':
          // Send the display name (filename)
          return field.displayValue || null;
        case 'date':
          // Ensure date is in YYYY-MM-DD format and valid
          try { return rawValue && !isNaN(new Date(rawValue).getTime()) ? rawValue.split('T')[0] : null; }
          catch (e) { return null; }
        case 'payment': // Usually excluded, but handle if needed elsewhere
          const n = parseFloat(rawValue); return isNaN(n) ? null : n;
        case 'tel': // Treat like text
        default: // text, email, textarea, select, radio
          // Return as string
          return String(rawValue);
      }
    },

    // Gets the display-friendly value for the summary sidebar
    getSummaryDisplayValue(field) {
      if (!field) return ''; // Return empty string if field is invalid

      // Get the raw value using the helper function (handles SetUnit quantity correctly)
      const rawValue = this.getRawValue(field);

      // Handle empty/null/undefined values first
      if (rawValue === null || rawValue === undefined || rawValue === '') {
        if (field.type === 'checkbox') return 'No'; // Display 'No' for unchecked checkbox
        if (field.type === 'checkbox-group') return '(None selected)';
        if (field.type === 'file') return '(No file selected)';
        // Display for empty quantity in SetUnit fields
        if (field.SetUnit) return '(Quantity not set)'; // Specific message for missing quantity
        return ''; // Default empty display
      }

      // Format based on field type for non-empty values
      switch (field.type) {
        case 'checkbox':
          // Display the first option text (e.g., 'Confirm') if checked, otherwise 'No'
          return rawValue === true ? (field.options?.[0] || 'Yes') : 'No';
        case 'checkbox-group':
          // Join selected option keys with a comma
          const selectedOptions = Object.entries(field.value || {}).filter(([, v]) => v === true).map(([k]) => k);
          return selectedOptions.length > 0 ? selectedOptions.join(', ') : '(None selected)';
        case 'file':
          // Show the display name (filename)
          return field.displayValue || '(No file selected)';
        case 'date':
          // Format date to locale-specific string (e.g., MM/DD/YYYY or DD/MM/YYYY)
          try { return rawValue && !isNaN(new Date(rawValue).getTime()) ? new Date(rawValue).toLocaleDateString() : ''; }
          catch (e) { return ''; }
        case 'tel': // Display telephone number as string
        default: // text, email, textarea, select, radio, payment, SetUnit (quantity)
          // Default to string representation
          return String(rawValue);
      }
    },

    // *** MODIFIED METHOD ***
    // Generates the summary text for SetUnit fields with installments, matching the requested format.
    getUnitInstallmentSummaryText(field) {
      // Ensure field is valid and has the correct flags
      if (!field || !field.SetUnit || !field.isInstallmentPayment) return '';

      // Get quantity, defaulting to 0 if null/invalid
      const quantity = Math.max(0, parseInt(field.quantity, 10) || 0);
      // Don't display if quantity is 0
      if (quantity === 0) return '(Quantity not set)';

      // Calculate total value for the item
      const itemTotal = (parseFloat(field.unitPrice) || 0) * quantity;
      // Get the amount entered by the user (can be null)
      const paidNowValue = field.installmentDetails?.amountPaidNow;

      // Determine the effective amount being paid now:
      // If user left input blank/0/null, they intend to pay the full item total.
      const isPayingFullByDefault = (paidNowValue == null || isNaN(paidNowValue) || paidNowValue <= 0) && itemTotal > 0;
      // Calculate the actual amount contributing to totalAmountPaidNow
      const effectivePaidNow = isPayingFullByDefault
          ? itemTotal // Paying full
          : Math.max(0, Math.min(paidNowValue || 0, itemTotal)); // Paying entered amount (clamped)

      // Calculate the remaining due specifically for this item
      const remainingDueForItem = Math.max(0, itemTotal - effectivePaidNow);

      // Construct the summary string in the desired format
      // Example: "Eggs: 3 (Paid Now: ZMW 50.00, Remaining Due: ZMW 100.00)"
      return `${quantity} (Paid Now: ${this.formatCurrency(effectivePaidNow)}, Remaining Due: ${this.formatCurrency(remainingDueForItem)})`;
    },

    // Calculates the remaining due amount specifically for one item (used in the form section)
    calculateItemRemainingDue(field) {
      // Only applicable to installment payment fields
      if (!field.isInstallmentPayment) return 0;

      let itemTotal = 0;
      let paidNow = 0;

      // Calculate total and paid amount based on whether it's SetUnit or not
      if (field.SetUnit) {
        const quantity = Math.max(0, parseInt(field.quantity, 10) || 0);
        itemTotal = (parseFloat(field.unitPrice) || 0) * quantity;
        const amountEntered = field.installmentDetails?.amountPaidNow; // Can be null

        // Determine amount paid: full if blank/0/null, otherwise entered amount (clamped)
        if (amountEntered == null || isNaN(amountEntered) || amountEntered <= 0) {
          paidNow = itemTotal > 0 ? itemTotal : 0; // Pay full if total > 0
        } else {
          paidNow = Math.max(0, Math.min(amountEntered, itemTotal)); // Clamp entered amount
        }
      } else { // Not SetUnit, but Installment
        itemTotal = parseFloat(field.value) || 0; // Total is stored in 'value'
        const amountEntered = field.installmentDetails?.amountPaidNow; // Can be null

        // Determine amount paid: 0 if blank/0/null, otherwise entered amount (clamped)
        if (amountEntered != null && !isNaN(amountEntered) && amountEntered > 0) {
          paidNow = Math.max(0, Math.min(amountEntered, itemTotal)); // Clamp entered amount
        } else {
          paidNow = 0; // Pay 0 if not specified
        }
      }

      // Return the difference, ensuring it's not negative
      return Math.max(0, itemTotal - paidNow);
    },

    // Handles errors when loading the company logo image
    onImageError(event) {
      console.warn("[DEBUG] Failed to load company logo. Using placeholder.");
      // Set the image source to the default placeholder URL
      event.target.src = this.defaultImageUrl;
    }
  }
};
</script>

<style scoped>
/* Basic Styling - Consider moving to a separate CSS file or using a framework like Tailwind */
.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f8f9fa; /* Light background */
  font-family: 'Inter', sans-serif; /* Example font */
}

.banner {
  background-color: #40434A; /* Dark banner background */
  padding: 1rem 0;
  color: #fff;
  text-align: center;
}

.banner-logo {
  max-height: 80px; /* Adjust logo size */
  width: auto;
  border-radius: 8px; /* Rounded corners for logo */
}

.main-content {
  display: flex;
  flex-wrap: wrap; /* Allow wrapping on smaller screens */
  gap: 2rem; /* Space between form and summary */
  padding: 2rem 1rem; /* Padding around content */
  flex-grow: 1;
}

.form-container {
  flex: 2; /* Takes up more space */
  min-width: 300px; /* Minimum width */
  background-color: #fff;
  padding: 2rem;
  border-radius: 12px; /* Rounded corners */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Soft shadow */
}

.summary-container {
  flex: 1; /* Takes up less space */
  min-width: 250px; /* Minimum width */
}

.form-title {
  text-align: center;
  margin-bottom: 2rem;
  color: #343a40;
  font-weight: 600;
}

.form-field {
  margin-bottom: 1.5rem;
}

.form-field label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #495057;
}

.form-input,
.form-input[type="text"],
.form-input[type="number"],
.form-input[type="email"],
.form-input[type="tel"], /* Added tel type */
.form-input[type="date"],
textarea.form-input,
select.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ced4da;
  border-radius: 8px; /* Rounded input fields */
  font-size: 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
  background-color: #fff; /* Ensure background is white */
}

.form-input:focus {
  border-color: #86b7fe;
  outline: 0;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.form-input:disabled {
  background-color: #e9ecef; /* Slightly greyed out when disabled */
  opacity: 0.7; /* Make it look disabled */
  cursor: not-allowed;
}


/* Specific input styling */
.date-input {
  /* Consider specific styling for date input appearance */
}
.quantity-input {
  max-width: 100px; /* Limit width of quantity */
  text-align: center;
}

.unit-input-group {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.unit-total {
  font-style: italic;
  color: #6c757d;
}

.installment-badge {
  font-size: 0.8em;
  background-color: #e9ecef;
  color: #495057;
  padding: 0.2em 0.5em;
  border-radius: 4px;
  margin-left: 0.5em;
  font-weight: normal;
}


.remaining-due {
  font-size: 0.9em;
  color: #dc3545; /* Warning color for due amount */
  font-weight: 500;
}

.help-text {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.required {
  color: #dc3545; /* Red asterisk */
  margin-left: 0.25rem;
}

/* Checkbox and Radio Styling */
.checkbox-field, .radio-option, .checkbox-option {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}
.checkbox-field input, .radio-option input, .checkbox-option input {
  margin-right: 0.5rem;
  cursor: pointer;
}
.checkbox-field input:disabled,
.radio-option input:disabled,
.checkbox-option input:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.checkbox-field label, .radio-option label, .checkbox-option label {
  margin-bottom: 0; /* Override default label margin */
  font-weight: normal;
  cursor: pointer;
}
.checkbox-field input:disabled + label,
.radio-option input:disabled + label,
.checkbox-option input:disabled + label {
  cursor: not-allowed;
  opacity: 0.7;
}

.radio-group, .checkbox-group {
  margin-top: 0.5rem;
}


/* File Input */
.file-field input[type="file"] {
  border: none;
  padding: 0;
}
.file-field input[type="file"]:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
.file-name-display {
  font-style: italic;
  color: #6c757d;
  margin-left: 10px;
}

.navigation-buttons {
  text-align: center;
  margin-top: 2rem;
}

.submit-btn {
  background-color: #0d6efd; /* Primary button color */
  color: white;
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 8px;
  font-size: 1.1rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-weight: 500;
  /* Add space for spinner */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem; /* Space between text and spinner */
}

.submit-btn:hover:not(:disabled) {
  background-color: #0b5ed7; /* Darker shade on hover */
}

.submit-btn:disabled {
  background-color: #6c757d; /* Disabled color */
  cursor: not-allowed;
  opacity: 0.65;
}

/* Simple Spinner (using Bootstrap's class structure for consistency) */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}
.spinner-border {
  display: inline-block;
  width: 2rem;
  height: 2rem;
  vertical-align: -0.125em;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  -webkit-animation: .75s linear infinite spinner-border;
  animation: .75s linear infinite spinner-border;
}

@keyframes spinner-border {
  to { transform: rotate(360deg); }
}

/* Visually hidden class (Bootstrap utility) */
.visually-hidden {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}


/* Summary Card Styling */
.card {
  border: none; /* Remove default card border */
  border-radius: 12px; /* Match form container */
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08); /* Soft shadow */
  background-color: #fff;
}

.card.sticky-top {
  top: 2rem; /* Add some space from the top when sticky */
}


.card-body {
  padding: 1.5rem;
}

.card-title {
  color: #343a40;
  font-weight: 600;
  margin-bottom: 1.5rem !important; /* Override bootstrap if needed */
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
  color: #495057;
}

.summary-item span:first-child {
  /* Style for label */
  margin-right: 1rem;
}
.summary-item span.fw-bold {
  /* Style for value */
  color: #212529;
  text-align: right;
}


.summary-item.total-amount {
  font-size: 1.1rem; /* Larger font for totals */
  margin-top: 1rem;
}
.summary-item.total-amount span.fw-bold {
  color: #0d6efd; /* Highlight total amount */
}
.summary-item.text-warning span.fw-bold {
  color: #ffc107; /* Warning color for remaining due */
}


hr {
  border-top: 1px solid #dee2e6; /* Standard separator */
}

/* === Responsive Adjustments === */
/* Mobile-first cascade: desktop rules first (min-width), then tablet & phone (max-width).
   The min-width rules establish the side-by-side desktop layout; the max-width rules
   progressively collapse it down to a single column on tablets and phones. */

/* Ultra-wide desktop: ≥ 1440px (e.g. 27" monitors, MacBook Pro) */
@media (min-width: 1440px) {
  .main-content { padding: 2.5rem 1.5rem; gap: 2.5rem; }
  .form-container { min-width: 700px; padding: 2.5rem; }
  .summary-container { min-width: 360px; }
  .form-field { max-width: 720px; }
  .form-input, textarea.form-input, select.form-input { max-width: 720px; }
  .form-title { font-size: 1.5rem; }
  .submit-btn { font-size: 1.15rem; padding: 0.9rem 2.25rem; }
}

/* Standard desktop: 1025–1439px */
@media (min-width: 1025px) {
  .form-container { min-width: 560px; }
  .summary-container { min-width: 320px; }
  /* Cap form fields so inputs don't stretch 1000px+ on wide screens */
  .form-field { max-width: 640px; }
  .form-input, textarea.form-input, select.form-input { max-width: 640px; }
  .custom-fields-section .form-field { max-width: 640px; }
  .multi-brand-section .brand-list-info { max-width: 360px; }
}

/* Tablet & small-laptop: ≤ 1024px (stack form + summary) */
@media (max-width: 1024px) {
  .main-content {
    flex-direction: column; /* Stack form and summary */
    gap: 1.5rem;
    padding: 1rem 0.75rem;
  }
  .form-container, .summary-container {
    flex: none; /* Reset flex grow */
    width: 100%; /* Take full width */
  }
  .form-container { padding: 1.5rem; }
  /* Sticky looks wrong when form + summary are stacked */
  .card.sticky-top { position: static; top: auto; }
  /* Make the submit button full-width and thumb-friendly */
  .submit-btn { width: 100%; }
  /* Reduce multi-brand section padding */
  .multi-brand-section { padding: 1rem; }
  /* Let qty + "Item Total" group wrap on tablet */
  .unit-input-group { flex-wrap: wrap; }
  .unit-total { width: 100%; }
}

/* Phone: ≤ 480px */
@media (max-width: 480px) {
  .banner { padding: 0.5rem 0; }
  .banner-logo { max-height: 56px; }
  .form-container { padding: 1rem; border-radius: 8px; }
  .form-title { font-size: 1.25rem; margin-bottom: 1.25rem; }
  .card-body { padding: 1rem; }
  .summary-item { font-size: 0.9rem; }
  .summary-item.total-amount { font-size: 1rem; }

  /* Brand list row: stack into 2 lines */
  .brand-list-row {
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.75rem;
  }
  .brand-list-info { flex: 1 1 100%; min-width: 0; }
  .brand-list-actions {
    width: 100%;
    justify-content: space-between;
    flex-wrap: wrap;
  }
  .brand-list-actions .line-total { min-width: 0; }
  .stock-exceeded-warning { width: 100%; }

  /* Multi-brand header stacks */
  .d-flex.justify-content-between.align-items-center.mb-3 {
    flex-wrap: wrap;
    gap: 0.5rem;
  }
}

/* SweetAlert Container - Position fixed for overlay */
#swal-container {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1060; /* Ensure it's above other content but potentially below modals */
}

/* Alert styling for price lookup errors */
.alert {
  padding: 1rem 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  color: #842029;
}

.alert-warning {
  background-color: #fff3cd;
  border: 1px solid #ffecb5;
  color: #664d03;
}

/* Multi-brand selection styles */
.multi-brand-section {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 12px;
}

.brand-list {
  display: flex;
  flex-direction: column;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.brand-list-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #e9ecef;
  transition: background-color 0.15s;
}

.brand-list-row:last-child {
  border-bottom: none;
}

.brand-list-row.selected {
  background-color: #f0f7ff;
}

.brand-list-info {
  flex: 1;
  min-width: 0;
}

.brand-list-info .brand-name {
  font-weight: 600;
  color: #212529;
  display: block;
  margin-bottom: 0.15rem;
}

.brand-list-info .brand-price {
  font-size: 0.85rem;
  color: #6c757d;
  margin-bottom: 0.15rem;
}

.brand-list-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

.brand-list-actions .line-total {
  min-width: 80px;
  text-align: right;
  font-weight: 600;
  color: #0d6efd;
}

.brand-card {
  background-color: #fff;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.brand-card.selected {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.brand-card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.brand-card-header input[type="checkbox"] {
  width: 18px;
  height: 18px;
}

.brand-name {
  font-weight: 600;
  color: #212529;
}

.brand-logo-img {
  width: 40px;
  height: 40px;
  object-fit: cover;
  border-radius: 4px;
  flex-shrink: 0;
}

.stock-exceeded-warning {
  display: block;
  font-size: 0.75rem;
  color: #dc3545;
  margin-top: 0.25rem;
  font-style: italic;
}

.brand-card-body {
  padding-left: 1.75rem;
}

.brand-price {
  font-size: 0.9rem;
  color: #495057;
  margin-bottom: 0.5rem;
}

.brand-quantity {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.qty-label {
  font-weight: 500;
  margin-bottom: 0;
}

.brand-quantity input {
  max-width: 80px;
}

.line-total {
  font-weight: 600;
  color: #0d6efd;
  margin-left: auto;
}

.brand-quantity-placeholder {
  font-size: 0.85rem;
}

.btn.btn-link {
  color: #0d6efd;
  text-decoration: none;
}

.btn.btn-link:hover {
  text-decoration: underline;
}
</style>
